import { useCallback, useEffect, useRef, useState } from 'react';
import { useEsriMapContext } from '../EsriMapContext';
import {
	watch,
	when,
	on,
	once,
	whenOnce,
} from '@arcgis/core/core/reactiveUtils.js';
import Extent from '@arcgis/core/geometry/Extent.js';
import EsriUtils from '../utils/esriHelpers';
import type { FitBoundsOptions, MapPosition } from '@Map/types';

export interface UseViewportOptions {
	/**
	 * Callback to fit the view to data extents (e.g. from useLayers().fitToDataExtents).
	 * When provided, resetPosition() will call this after clearing the external-position flag.
	 */
	fitToDataExtents?: () => void;
	/** Called with [xmin, ymin, xmax, ymax] after each pan/zoom settles. */
	onBoundsChange?: (bounds: number[]) => void;
	/** Called with { center, zoom } after each pan/zoom settles. */
	onPositionChange?: (position: MapPosition) => void;
	/** Initial viewport bounds [swLng, swLat, neLng, neLat] applied when no external position is set. */
	defaultBounds?: number[];
	/** When true, defaultBounds is ignored (consumer has supplied an explicit position). */
	hasExternalPosition?: boolean;
	fitBoundsOptions?: FitBoundsOptions;
}

export const useViewport = (options: UseViewportOptions = {}) => {
	const { view } = useEsriMapContext();
	const {
		fitToDataExtents,
		onBoundsChange,
		onPositionChange,
		defaultBounds,
		hasExternalPosition,
		fitBoundsOptions,
	} = options;
	const [positionSetExternally, setPositionSetExternally] = useState(false);
	const lastBoundsRef = useRef<string | null>(null);
	const lastPositionRef = useRef<string | null>(null);

	useEffect(() => {
		if (!view || !onBoundsChange) return;

		const handle = watch(
			() => view.stationary,
			stationary => {
				if (!stationary) return;
				const bounds = EsriUtils.extentToBounds(view.extent);
				const key = JSON.stringify(bounds);
				if (key !== lastBoundsRef.current) {
					lastBoundsRef.current = key;
					onBoundsChange(bounds);
				}
			},
		);

		return () => handle.remove();
	}, [view, onBoundsChange]);

	useEffect(() => {
		if (!view || !onPositionChange) return;

		const handle = watch(
			() => view.stationary,
			stationary => {
				if (!stationary) return;
				const { longitude, latitude } = view.center;
				if (longitude == null || latitude == null) return;
				const position: MapPosition = {
					center: [longitude, latitude],
					zoom: view.zoom,
				};
				const key = JSON.stringify(position);
				if (key !== lastPositionRef.current) {
					lastPositionRef.current = key;
					onPositionChange(position);
				}
			},
		);

		return () => handle.remove();
	}, [view, onPositionChange]);

	useEffect(() => {
		if (!view || !fitBoundsOptions?.padding) return;
		const p = fitBoundsOptions.padding;
		view.padding =
			typeof p === 'number'
				? { top: p, right: p, bottom: p, left: p }
				: {
						top: p.top ?? 0,
						right: p.right ?? 0,
						bottom: p.bottom ?? 0,
						left: p.left ?? 0,
					};
	}, [view, fitBoundsOptions?.padding]);

	useEffect(() => {
		if (!view || hasExternalPosition) return;
		if (!defaultBounds || defaultBounds.length !== 4) return;
		if (defaultBounds.every(v => v === 0)) return;
		const [xmin, ymin, xmax, ymax] = defaultBounds;
		const promise = view.goTo(
			new Extent({
				xmin,
				ymin,
				xmax,
				ymax,
				spatialReference: { wkid: 4326 },
			}),
			{ duration: 0 },
		);
		if (fitBoundsOptions?.maxZoom !== undefined) {
			const { maxZoom } = fitBoundsOptions;
			void promise.then(() => {
				if (view.zoom > maxZoom) {
					void view.goTo({ zoom: maxZoom }, { duration: 0 });
				}
			});
		}
	}, [view, defaultBounds, hasExternalPosition]);

	const setPosition = useCallback(
		({ center, zoom }: { center: number[]; zoom: number }) => {
			setPositionSetExternally(true);
			if (view) {
				view.goTo({ center, zoom });
			}
		},
		[view],
	);

	const resetPosition = useCallback(() => {
		setPositionSetExternally(false);
		fitToDataExtents?.();
	}, [fitToDataExtents]);

	return {
		setPosition,
		resetPosition,
		positionSetExternally,
	};
};

export type Viewport = ReturnType<typeof useViewport>;
